import type { Extension } from '@codemirror/state';
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { EditorView } from '@codemirror/view';
import { tags as t } from '@lezer/highlight';
import {
	generalCursor,
	generalDiff,
	generalGutter,
	generalLine,
	generalMatching,
	generalPanel,
	generalPlaceholder,
	generalScroller,
	generalSearchField,
	generalTooltip,
} from './utils';

export interface SyntaxPalette {
	keyword: string;
	controlKeyword: string;
	variable: string;
	type: string;
	function: string;
	number: string;
	string: string;
	comment: string;
	regex: string;
	modified: string;
}

export function createTheme(syntax: SyntaxPalette, isDark: boolean): Extension {
	const editorTheme = EditorView.theme(
		{
			'&': {
				color: 'var(--ui-text)',
				backgroundColor: 'var(--ui-bg)',
			},

			'.cm-content': {
				caretColor: 'var(--ui-text-highlighted)',
			},
			'.cm-cursor, .cm-dropCursor': {
				borderLeftColor: 'var(--ui-text-highlighted)',
				borderLeftWidth: generalCursor.borderLeftWidth,
			},
			'.cm-fat-cursor': {
				backgroundColor: 'color-mix(in srgb, var(--ui-text-highlighted) 60%, transparent)',
				color: 'var(--ui-bg)',
			},

			'&.cm-focused > .cm-scroller > .cm-selectionLayer .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection': {
				backgroundColor: 'color-mix(in srgb, var(--ui-primary) 25%, transparent)',
			},

			'.cm-selectionLayer': {
				zIndex: 100,
			},

			'.cm-searchMatch': {
				backgroundColor: 'color-mix(in srgb, var(--ui-primary) 25%, transparent)',
				outline: '1px solid color-mix(in srgb, var(--ui-primary) 55%, transparent)',
				color: 'var(--ui-text-highlighted)',
				borderRadius: generalSearchField.borderRadius,
			},
			'.cm-searchMatch.cm-searchMatch-selected': {
				'backgroundColor': 'color-mix(in srgb, var(--ui-primary) 55%, transparent)',
				'color': 'var(--ui-text-highlighted)',
				'padding': generalSearchField.padding,

				'& span': {
					color: 'var(--ui-text-highlighted)',
				},
			},
			'.cm-search.cm-panel.cm-textfield': {
				color: 'var(--ui-text)',
				borderRadius: generalSearchField.borderRadius,
				padding: generalSearchField.padding,
			},

			'.cm-panels': {
				backgroundColor: 'var(--ui-bg-elevated)',
				color: 'var(--ui-text)',
				borderRadius: '3px',
				boxShadow: '0 2px 6px color-mix(in srgb, var(--ui-bg-inverted) 20%, transparent)',
			},
			'.cm-panels.cm-panels-top': {
				borderBottom: '1px solid var(--ui-border)',
			},
			'.cm-panels.cm-panels-bottom': {
				borderTop: '1px solid var(--ui-border)',
			},
			'.cm-panel button': {
				backgroundColor: 'var(--ui-bg-accented)',
				color: 'var(--ui-text)',
				border: '1px solid var(--ui-border)',
				borderRadius: generalPanel.borderRadius,
				padding: generalPanel.padding,
			},
			'.cm-panel button:hover': {
				backgroundColor: 'var(--ui-bg-elevated)',
				border: '1px solid var(--ui-border-accented)',
			},

			'.cm-activeLine': {
				backgroundColor: 'color-mix(in srgb, var(--ui-text-dimmed) 8%, transparent)',
				borderRadius: generalLine.borderRadius,
				zIndex: 1,
			},

			'.cm-gutters': {
				backgroundColor: 'var(--ui-bg-muted)',
				color: 'var(--ui-text-dimmed)',
				border: 'none',
				paddingLeft: '0.5rem',
				paddingRight: generalGutter.paddingRight,
				userSelect: 'none',
				zIndex: 0,
			},
			'.cm-activeLineGutter': {
				backgroundColor: 'color-mix(in srgb, var(--ui-text-dimmed) 8%, transparent)',
				color: 'var(--ui-text-highlighted)',
				fontWeight: generalGutter.fontWeight,
			},
			'.cm-lineNumbers': {
				fontSize: generalGutter.fontSize,
				lineHeight: generalGutter.lineHeight,
			},
			'.cm-foldGutter': {
				fontSize: generalGutter.fontSize,
				lineHeight: generalGutter.lineHeight,
			},
			'.cm-foldGutter .cm-gutterElement': {
				color: 'var(--ui-text-dimmed)',
				cursor: 'pointer',
			},
			'.cm-foldGutter .cm-gutterElement:hover': {
				color: 'var(--ui-text-highlighted)',
			},

			'.cm-insertedLine': {
				textDecoration: generalDiff.insertedTextDecoration,
				backgroundColor: 'color-mix(in srgb, var(--ui-success) 20%, transparent)',
				color: 'var(--ui-success)',
				padding: generalDiff.insertedLinePadding,
				borderRadius: generalDiff.borderRadius,
			},
			'ins.cm-insertedLine, ins.cm-insertedLine:not(:has(.cm-changedText))': {
				textDecoration: generalDiff.insertedTextDecoration,
				backgroundColor: 'color-mix(in srgb, var(--ui-success) 20%, transparent) !important',
				color: 'var(--ui-success)',
				padding: generalDiff.insertedLinePadding,
				borderRadius: generalDiff.borderRadius,
				border: '1px solid color-mix(in srgb, var(--ui-success) 25%, transparent)',
			},
			'ins.cm-insertedLine .cm-changedText': {
				background: 'transparent !important',
			},

			'.cm-deletedLine': {
				textDecoration: generalDiff.deletedTextDecoration,
				backgroundColor: 'color-mix(in srgb, var(--ui-error) 20%, transparent)',
				color: 'var(--ui-error)',
				padding: generalDiff.insertedLinePadding,
				borderRadius: generalDiff.borderRadius,
			},
			'del.cm-deletedLine, del, del:not(:has(.cm-deletedText))': {
				textDecoration: generalDiff.deletedTextDecoration,
				backgroundColor: 'color-mix(in srgb, var(--ui-error) 20%, transparent) !important',
				color: 'var(--ui-error)',
				padding: generalDiff.insertedLinePadding,
				borderRadius: generalDiff.borderRadius,
				border: '1px solid color-mix(in srgb, var(--ui-error) 25%, transparent)',
			},
			'del .cm-deletedText, del .cm-changedText': {
				background: 'transparent !important',
			},

			'.cm-tooltip': {
				backgroundColor: 'var(--ui-bg-elevated)',
				border: '1px solid var(--ui-border)',
				borderRadius: generalTooltip.borderRadius,
				padding: generalTooltip.padding,
				boxShadow: '0 3px 8px color-mix(in srgb, var(--ui-bg-inverted) 20%, transparent)',
			},
			'.cm-tooltip-autocomplete': {
				'& > ul': {
					backgroundColor: 'var(--ui-bg-elevated)',
					border: 'none',
					maxHeight: '300px',
				},
				'& > ul > li': {
					padding: generalTooltip.padding,
					lineHeight: generalTooltip.lineHeight,
				},
				'& > ul > li[aria-selected]': {
					backgroundColor: 'color-mix(in srgb, var(--ui-primary) 20%, transparent)',
					color: 'var(--ui-text-highlighted)',
					borderRadius: generalTooltip.borderRadiusSelected,
				},
				'& > ul > li > span.cm-completionIcon': {
					color: 'var(--ui-text-dimmed)',
					paddingRight: generalTooltip.paddingRight,
				},
				'& > ul > li > span.cm-completionDetail': {
					color: 'var(--ui-text-dimmed)',
					fontStyle: 'italic',
				},
			},
			'.cm-tooltip .cm-tooltip-arrow:before': {
				borderTopColor: 'transparent',
				borderBottomColor: 'transparent',
			},
			'.cm-tooltip .cm-tooltip-arrow:after': {
				borderTopColor: 'var(--ui-bg-elevated)',
				borderBottomColor: 'var(--ui-bg-elevated)',
			},

			'.cm-diagnostic': {
				'&-error': {
					borderLeft: '3px solid var(--ui-error)',
				},
				'&-warning': {
					borderLeft: '3px solid var(--ui-warning)',
				},
				'&-info': {
					borderLeft: '3px solid var(--ui-info)',
				},
			},
			'.cm-lintPoint-error': {
				borderBottom: '2px wavy var(--ui-error)',
			},
			'.cm-lintPoint-warning': {
				borderBottom: '2px wavy var(--ui-warning)',
			},

			'.cm-matchingBracket': {
				backgroundColor: 'color-mix(in srgb, var(--ui-primary) 15%, transparent)',
				outline: '1px solid color-mix(in srgb, var(--ui-primary) 50%, transparent)',
				borderRadius: generalMatching.borderRadius,
			},
			'.cm-nonmatchingBracket': {
				backgroundColor: 'color-mix(in srgb, var(--ui-error) 25%, transparent)',
				outline: '1px solid var(--ui-error)',
				borderRadius: generalMatching.borderRadius,
			},

			'.cm-selectionMatch': {
				backgroundColor: 'color-mix(in srgb, var(--ui-text) 15%, transparent)',
				outline: '1px solid color-mix(in srgb, var(--ui-border) 45%, transparent)',
				borderRadius: generalMatching.borderRadius,
			},

			'.cm-foldPlaceholder': {
				backgroundColor: 'var(--ui-bg-elevated)',
				color: 'var(--ui-text-dimmed)',
				border: '1px dotted color-mix(in srgb, var(--ui-text-dimmed) 45%, transparent)',
				borderRadius: generalPlaceholder.borderRadius,
				padding: generalPlaceholder.padding,
				margin: generalPlaceholder.margin,
			},

			'&.cm-focused': {
				outline: 'none',
				boxShadow: '0 0 0 1px var(--ui-border)',
			},

			'& .cm-scroller::-webkit-scrollbar': {
				width: generalScroller.width,
				height: generalScroller.height,
			},
			'& .cm-scroller::-webkit-scrollbar-track': {
				background: 'var(--ui-bg)',
			},
			'& .cm-scroller::-webkit-scrollbar-thumb': {
				backgroundColor: 'var(--ui-scrollbar)',
				borderRadius: generalScroller.borderRadius,
				border: '3px solid var(--ui-bg)',
			},

			'.cm-ghostText': {
				opacity: '0.5',
				color: 'var(--ui-text-dimmed)',
			},
		},
		{ dark: isDark },
	);

	const highlightStyle = HighlightStyle.define([
		{ tag: t.keyword, color: syntax.keyword, fontWeight: 'bold' },
		{ tag: t.controlKeyword, color: syntax.controlKeyword, fontWeight: 'bold' },
		{ tag: t.moduleKeyword, color: syntax.keyword, fontWeight: 'bold' },

		{ tag: [t.name, t.character, t.macroName], color: 'var(--ui-text)' },
		{ tag: [t.variableName], color: syntax.variable },
		{ tag: [t.propertyName], color: syntax.variable, fontStyle: 'normal' },

		{ tag: [t.typeName], color: syntax.type },
		{ tag: [t.className], color: syntax.type, fontStyle: 'normal' },
		{ tag: [t.namespace], color: 'var(--ui-text)', fontStyle: 'normal' },

		{ tag: [t.operator, t.operatorKeyword], color: 'var(--ui-text)' },
		{ tag: [t.bracket], color: 'var(--ui-text)' },
		{ tag: [t.brace], color: 'var(--ui-text)' },
		{ tag: [t.punctuation], color: 'var(--ui-text)' },

		{ tag: [t.function(t.variableName)], color: syntax.function },
		{ tag: [t.labelName], color: syntax.function, fontStyle: 'normal' },
		{ tag: [t.definition(t.function(t.variableName))], color: syntax.function },
		{ tag: [t.definition(t.variableName)], color: syntax.variable },

		{ tag: t.number, color: syntax.number },
		{ tag: t.changed, color: syntax.modified },
		{ tag: t.annotation, color: syntax.modified, fontStyle: 'italic' },
		{ tag: t.modifier, color: syntax.keyword, fontStyle: 'normal' },
		{ tag: t.self, color: syntax.keyword },
		{ tag: [t.color, t.constant(t.name), t.standard(t.name)], color: syntax.variable },
		{ tag: [t.atom, t.bool, t.special(t.variableName)], color: syntax.keyword },

		{ tag: [t.processingInstruction, t.inserted], color: syntax.string },
		{ tag: [t.special(t.string), t.regexp], color: syntax.regex },
		{ tag: t.string, color: syntax.string },

		{ tag: t.definition(t.typeName), color: syntax.type, fontWeight: 'bold' },
		{ tag: [t.definition(t.name), t.separator], color: 'var(--ui-text)' },

		{ tag: t.meta, color: 'var(--ui-text-dimmed)' },
		{ tag: t.comment, fontStyle: 'italic', color: syntax.comment },
		{ tag: t.docComment, fontStyle: 'italic', color: syntax.comment },

		{ tag: [t.tagName], color: syntax.keyword },
		{ tag: [t.attributeName], color: syntax.variable },

		{ tag: [t.heading], fontWeight: 'bold', color: syntax.keyword },
		{ tag: t.heading1, color: syntax.keyword, fontWeight: 'bold' },
		{ tag: t.heading2, color: syntax.keyword },
		{ tag: t.heading3, color: syntax.keyword },
		{ tag: t.heading4, color: syntax.keyword },
		{ tag: t.heading5, color: syntax.keyword },
		{ tag: t.heading6, color: syntax.keyword },
		{ tag: [t.strong], fontWeight: 'bold', color: syntax.keyword },
		{ tag: [t.emphasis], fontStyle: 'italic', color: syntax.type },

		{ tag: [t.link], color: 'var(--ui-primary)', textDecoration: 'underline', textUnderlinePosition: 'under' },
		{ tag: [t.url], color: 'var(--ui-info)', textDecoration: 'underline', textUnderlineOffset: '2px' },

		{ tag: [t.invalid], color: 'var(--ui-text)', textDecoration: 'underline wavy', borderBottom: '1px wavy var(--ui-error)' },
		{ tag: [t.strikethrough], color: 'var(--ui-error)', textDecoration: 'line-through' },

		{ tag: t.constant(t.name), color: syntax.variable },
		{ tag: t.deleted, color: 'var(--ui-error)' },
		{ tag: t.squareBracket, color: 'var(--ui-text)' },
		{ tag: t.angleBracket, color: 'var(--ui-text)' },

		{ tag: t.monospace, color: 'var(--ui-text)' },
		{ tag: [t.contentSeparator], color: 'var(--ui-text)' },
		{ tag: t.quote, color: syntax.comment },
	]);

	return [editorTheme, syntaxHighlighting(highlightStyle)];
}
